/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ColumnsType } from 'antd/es/table';

import { GetNodesResponse, Node } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { ConnectivityTime } from '@/components/States/ConnectivityTime';
import { OperationalState } from '@/components/States/OperationalState';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { OsIcon } from '@/primitives/Icons';
import { Link } from '@/primitives/Link';
import { Title } from '@/primitives/Title';
import { Tooltip } from '@/primitives/Tooltip';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type SubtenantNodesProps = {
  nodesQuery: ReturnType<typeof usePaginatedNodesQuery>;
};

export function SubtenantNodes({ nodesQuery }: SubtenantNodesProps) {
  const { t } = useMimicTranslation('nodes');
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const nodesColumns: ColumnsType<Node> = [
    {
      title: t('table.header.host_name'),
      dataIndex: 'hostname',
      key: 'hostname',
      sorter: true,
      render: (hostname: string, record: Node) => {
        const hostName = hostname || t('table.notAvailable');
        return (
          <Link
            to={`/tenants/${tenantId}/nodes/${record.id}`}
            style={{ fontSize: 'inherit', fontFamily: 'DM Mono' }}
            dataTestId="node-link"
          >
            {hostName}
          </Link>
        );
      },
      width: '400px',
    },
    {
      title: t('table.header.operationalState'),
      dataIndex: 'appliedOperationalState',
      key: 'state',
      sorter: true,
      render: (_, node) => <OperationalState state={node.appliedRevisionState!.operationalState} />,
      width: '200px',
    },
    {
      title: t('table.header.os'),
      dataIndex: 'operatingSystem',
      key: 'os',
      sorter: true,
      filters: [
        { text: 'Windows', value: 'windows' },
        { text: 'Linux', value: 'linux' },
      ],
      render: (operatingSystem: Node['operatingSystem'], node: Node) => {
        const osTooltip = operatingSystem || '';
        const osVersionTooltip = node.systemProfile?.['os.version'] || '';
        const tooltip = osTooltip || osVersionTooltip ? `${osTooltip} ${osVersionTooltip}` : '';
        return (
          <Tooltip title={tooltip.trim()} placement="right">
            <div data-testid="operating-system-icon" aria-label={tooltip.trim() || '-'}>
              <OsIcon name={operatingSystem} />
            </div>
          </Tooltip>
        );
      },
      width: '100px',
    },
    {
      title: t('table.header.nodeVersion'),
      dataIndex: 'appliedConfiguration',
      key: 'nodeVersion',
      sorter: true,
      render: (_, node: Node) => {
        const configRevisionNumber = node.appliedRevisionState?.configRevision?.revisionNumber;
        if (!configRevisionNumber) return t('table.notAvailable');
        return `v${configRevisionNumber}`;
      },
      width: '150px',
    },
    {
      title: t('table.header.lastSeen'),
      dataIndex: 'lastSeen',
      key: 'lastUpdated',
      sorter: true,
      render: (_, record: Node) => {
        return (
          <ConnectivityTime
            state={record.connectivityState}
            lastSeen={record.lastSeen}
            heartbeatStates={record.heartbeatStates}
          />
        );
      },
    },
  ];

  return (
    <Container style={{ marginTop: '24px' }}>
      <Title level={3}>{t('title')}</Title>
      <QueryWrapper<GetNodesResponse> queryResult={nodesQuery}>
        {(data) => (
          <PaginatedTable
            data={data}
            columns={nodesColumns}
            rowKey={(node: Node) => node.id}
            onParamsChange={nodesQuery.updateQueryParams}
            style={{ width: '100%' }}
          />
        )}
      </QueryWrapper>
    </Container>
  );
}
